import { useEffect, useRef, useState } from 'react';

export interface UseRouteChangeCallbackParams {
  currentPath: string, previousPath?: string, currentSearch?: string, previousSearch: string
}


export const useRouteChange = (
  callback: ({currentPath, previousPath, currentSearch, previousSearch}: UseRouteChangeCallbackParams) => void,
  immediate: boolean = false,
  debounceMs: number = 200 // Ajoute un paramètre debounce
) => {
  const [pathname, setPathname] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const [search, setSearch] = useState(window.location.search);
  const previousPathRef = useRef<string>();
  const previousSearchRef = useRef<string>();
  const callbackRef = useRef(callback);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedCallback = (params: UseRouteChangeCallbackParams) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      callbackRef.current(params);
    }, debounceMs);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentPath = window.location.pathname;
    const previousPath = previousPathRef.current;
    const previousSearch = previousSearchRef.current || '';
    const currentSearch = window.location.search;

    debouncedCallback({currentPath, previousPath, currentSearch, previousSearch});

    previousPathRef.current = currentPath;
    previousSearchRef.current = currentSearch;
    setPathname(currentPath);
  }, [immediate]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      const newPath = window.location.pathname;
      const newSearch = window.location.search;
      const previousPath = previousPathRef.current;
      const previousSearch = previousSearchRef.current || '';
      
      if (newPath !== previousPath || newSearch !== previousSearch) {
        debouncedCallback({
          currentPath: newPath,
          previousPath,
          currentSearch: newSearch,
          previousSearch
        });
        previousPathRef.current = newPath;
        previousSearchRef.current = newSearch;
        setPathname(newPath);
        setSearch(newSearch);
      }
    };

    let lastPath = window.location.pathname;
    let lastSearch = window.location.search;
    
    const checkPathChange = () => {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      
      if (currentPath !== lastPath || currentSearch !== lastSearch) {
        debouncedCallback({
          currentPath,
          previousPath: lastPath,
          currentSearch,
          previousSearch: lastSearch
        });
        previousPathRef.current = currentPath;
        previousSearchRef.current = currentSearch;
        setPathname(currentPath);
        setSearch(currentSearch);
        lastPath = currentPath;
        lastSearch = currentSearch;
      }
    };

    const pathCheckInterval = setInterval(checkPathChange, 100);

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(pathCheckInterval);
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, []);

  return { pathname, search };
};